// ============================================================
// TaskFlow OS - Profile Page
// ============================================================

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { IconUser, IconEdit, IconCheck, IconUpload, IconShield } from '../icons/SvgIcons';
import { ROLE_LABELS } from '../lib/constants';

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',
    department: user?.department || '',
    position: user?.position || '',
    phone: user?.phone || '',
  });

  const handleSave = async () => {
    setSaving(true);
    const result = await updateProfile(form);
    setSaving(false);
    if (!result.error) {
      setEditing(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const ext = file.name.split('.').pop();
    const path = `avatars/${user.id}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true });

    if (uploadError) return;

    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    await updateProfile({ avatar_url: data.publicUrl });
  };

  if (!user) return null;

  return (
    <>
      <Header title="プロフィール" subtitle="アカウント情報の管理" />

      <div className="p-6 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card"
        >
          {/* Avatar Section */}
          <div className="flex items-center gap-6 mb-8 pb-6 border-b border-white/5">
            <div className="relative group">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.display_name}
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-white/10"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border-2 border-white/10 flex items-center justify-center">
                  <IconUser size={32} color="#10b981" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <IconUpload size={20} className="text-white" />
                <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
              </label>
            </div>

            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{user.display_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                >
                  <span className="flex items-center gap-1">
                    <IconShield size={10} />
                    {ROLE_LABELS[user.role] || user.role}
                  </span>
                </span>
                {user.department && (
                  <span className="text-xs text-white/40">{user.department}</span>
                )}
              </div>
              {user.bio && <p className="text-xs text-white/50 mt-2">{user.bio}</p>}
            </div>

            <button
              onClick={() => setEditing(!editing)}
              className="glass-btn flex items-center gap-2 text-sm"
            >
              <IconEdit size={14} />
              {editing ? 'キャンセル' : '編集'}
            </button>
          </div>

          {/* Profile Form */}
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-white/50 mb-1.5">表示名</label>
                {editing ? (
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    className="glass-input"
                  />
                ) : (
                  <p className="text-sm text-white py-2">{user.display_name}</p>
                )}
              </div>

              <div>
                <label className="block text-xs text-white/50 mb-1.5">部署</label>
                {editing ? (
                  <input
                    type="text"
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="glass-input"
                    placeholder="例: 開発部"
                  />
                ) : (
                  <p className="text-sm text-white/60 py-2">{user.department || '未設定'}</p>
                )}
              </div>

              <div>
                <label className="block text-xs text-white/50 mb-1.5">役職</label>
                {editing ? (
                  <input
                    type="text"
                    value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    className="glass-input"
                    placeholder="例: エンジニア"
                  />
                ) : (
                  <p className="text-sm text-white/60 py-2">{user.position || '未設定'}</p>
                )}
              </div>

              <div>
                <label className="block text-xs text-white/50 mb-1.5">電話番号</label>
                {editing ? (
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="glass-input"
                    placeholder="例: 090-1234-5678"
                  />
                ) : (
                  <p className="text-sm text-white/60 py-2">{user.phone || '未設定'}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5">自己紹介</label>
              {editing ? (
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="glass-textarea"
                  placeholder="簡単な自己紹介"
                />
              ) : (
                <p className="text-sm text-white/60 py-2">{user.bio || '未設定'}</p>
              )}
            </div>

            {editing && (
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="glass-btn-primary flex items-center gap-2 text-sm"
                >
                  <IconCheck size={14} />
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            )}
          </div>

          {/* Login Info */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">ログイン情報</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-white/30">最終ログイン</span>
                <p className="text-white/60 mt-0.5">
                  {user.last_login_at ? new Date(user.last_login_at).toLocaleString('ja-JP') : '---'}
                </p>
              </div>
              <div>
                <span className="text-white/30">IPアドレス</span>
                <p className="text-white/60 mt-0.5 font-mono">{user.last_login_ip || '---'}</p>
              </div>
              <div>
                <span className="text-white/30">ロケーション</span>
                <p className="text-white/60 mt-0.5">{user.last_login_location || '---'}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default Profile;
